import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  Button,
  Input,
  FormControl,
  FormLabel,
  Textarea,
  Flex,
  Box,
  Text,
  InputGroup,
  useToast,
  Select,
} from "@chakra-ui/react";
import { CreatableSelect } from "chakra-react-select";
import { AlertsContext } from "providers/alerts";
import { ChecklistContext } from "providers/checklist";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { selectChakraStyles } from "utils/constants";
import ItemContacts from "../ItemContacts";
import ItemDocuments from "../ItemDocuments";
import DeleteItemBtn from "./DeleteItemBtn";
import ItemTemplate from "../ItemTemplate";
import colors from "utils/colors";

import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";
import { ChecklistStatus } from "hooks/useChecklist";
import { useRoles } from "hooks/useRoles";
import BorrowerStatusDetail from "./BorrowerStatusDetail";

const EditItemDrawer = ({ canEdit }: { canEdit: boolean }) => {
  const { user } = useTenant();
  const { editItem, setEditItem, categories, saveItem, statuses } = useContext(ChecklistContext);

  const location = useLocation();
  let params = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const last_status = editItem?.loan_item_status?.at?.(-1)?.status;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { showError } = useContext(AlertsContext);

  const [title, setTitle] = useState(editItem?.title || "");

  const [description, setDescription] = useState(
    (editItem?.checklist_item?.description || editItem?.description || "").replace(/\\n/g, "\n")
  );
  const [category, setCategory] = useState(editItem?.category || "");
  const [status, setStatus] = useState(last_status ?? ChecklistStatus.Pending);
  const [startDate, setStartDate] = useState(editItem?.start_date || "");
  const [dueDate, setDueDate] = useState(editItem?.due_date || "");
  const [contacts, setContacts] = useState(editItem?.contacts || []);
  const [notes, setNotes] = useState(editItem?.notes || "");
  const [documents, setDocuments] = useState(editItem?.documents || []);
  const [loading, setLoading] = useState(false);

  const { roles } = useRoles({
    company_id: user?.company?.id,
  });

  const toast = useToast();

  useEffect(() => {
    if (editItem.id.toString() === params.get("item")) {
      onOpen();
    }
  }, []);

  const saveChanges = async () => {
    setLoading(true);

    if ((!startDate && dueDate) || (!dueDate && startDate)) {
      toast({
        title: "Validation Error",
        description: "Please select both Start Date and Due Date.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    if (new Date(startDate) > new Date(dueDate)) {
      toast({
        title: "Validation Error",
        description: "Due Date cannot be earlier than Start Date.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      await saveItem({
        title,
        category,
        status,
        notes,
        documents,
        contacts,
        last_status,
        start_date: startDate === "" ? null : startDate,
        due_date: dueDate === "" ? null : dueDate,
        description,
      });
    } catch (err) {
      showError({
        title: "Update Failed",
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  };

  const closeDrawer = () => {
    setEditItem(null);
    onClose();
  };

  useEffect(() => {
    if (editItem) {
      onOpen();
    } else {
      onClose();
    }
  }, [editItem]);

  const borrowerRoles = ["Administrator", "Borrower"];

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={closeDrawer} size="md">
      <DrawerOverlay />
      <DrawerContent maxWidth="555px">
        <DrawerCloseButton onClick={closeDrawer} />
        <DrawerHeader pb="0">{title ?? ""}</DrawerHeader>
        <DrawerBody>
          <Text fontSize="14px" color="gray.600" mb="16px">
            {description}
          </Text>

          {user.type !== "lender" && (
            <BorrowerStatusDetail
              status={status}
              startDate={startDate}
              dueDate={dueDate}
              category={category}
            />
          )}
          {user.type === "lender" && (
            <>
              <FormControl mb="20px">
                <FormLabel>Item Name</FormLabel>
                <Input
                  variant="primary"
                  value={title}
                  onChange={ev => setTitle(ev.target.value)}
                  placeholder="Type here..."
                />
              </FormControl>
              {user?.role?.name === "Administrator" && (
                <FormControl mb="20px">
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    variant="primary"
                    value={description}
                    onChange={ev => setDescription(ev.target.value)}
                    placeholder="Type here..."
                    rows={3}
                  />
                </FormControl>
              )}
            </>
          )}
          {user.type === "lender" && (
            <>
              <Box display="flex" flexDirection="row" gap="16px">
                <FormControl mb="20px">
                  <FormLabel>Category</FormLabel>
                  <CreatableSelect
                    useBasicStyles
                    selectedOptionColor="black"
                    colorScheme="purple"
                    variant="outline"
                    chakraStyles={selectChakraStyles}
                    value={{
                      label: category,
                      value: category,
                    }}
                    hideSelectedOptions
                    focusBorderColor={colors.GRAY_50}
                    onChange={(newValue: any) => setCategory(newValue.value)}
                    options={categories.map((option: string) => ({
                      label: option,
                      value: option,
                    }))}
                    onCreateOption={setCategory}
                    components={{
                      ClearIndicator: () => null,
                    }}
                  />
                </FormControl>
                <FormControl mb="20px">
                  <FormLabel>Status</FormLabel>
                  <Select
                    value={status}
                    onChange={e => setStatus(e.target.value)}
                    focusBorderColor={colors.GRAY_50}
                    borderColor={colors.GRAY_50}
                    borderRadius="8px"
                    borderWidth="1px"
                  >
                    {statuses.map((option: string) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box display="flex" flexDirection="row" gap="16px">
                <FormControl mb="20px">
                  <FormLabel>Start Date</FormLabel>
                  <InputGroup>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={ev => setStartDate(ev.target.value)}
                      focusBorderColor={colors.GRAY_50}
                      borderColor={colors.GRAY_50}
                      borderRadius="8px"
                      borderWidth="1px"
                    />
                  </InputGroup>
                </FormControl>
                <FormControl mb="20px">
                  <FormLabel>Due Date</FormLabel>
                  <InputGroup>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={ev => setDueDate(ev.target.value)}
                      focusBorderColor={colors.GRAY_50}
                      borderColor={colors.GRAY_50}
                      borderRadius="8px"
                      borderWidth="1px"
                    />
                  </InputGroup>
                </FormControl>
              </Box>
            </>
          )}
          {/* Info box */}
          {/* <Box mb="20px">
            <Text as="h3" fontSize="14px" color="black" mb="8px" fontWeight="500">
              View Options
            </Text>
            <Flex
              background="#DFEEFF"
              borderRadius="8px"
              p="16px"
              border="1px solid #0D69D4"
              gap="16px"
              alignItems="flex-start"
            >
              <FontAwesomeIcon icon={["far", "circle-info"]} size="xl" color="#0D69D4" />
              Choose who can view and upload documents for this checklist
              item. Lender Administrator roles cannot be removed from
              checklist items.
            </Flex>
          </Box> */}
          {/* Lender Roles */}
          {/* <Box mb="20px">
            <Text as="h3" fontSize="14px" color="black" mb="8px" fontWeight="500">
              Select Lender Roles
            </Text>
            <CreatableSelect
              useBasicStyles
              selectedOptionColor="black"
              colorScheme="purple"
              variant="outline"
              chakraStyles={selectChakraStyles}
              value={{
                label: lenderRole,
                value: lenderRole,
              }}
              hideSelectedOptions
              focusBorderColor={colors.GRAY_50}
              onChange={(newValue: any) => setLenderRole(newValue.value)}
              options={lenderRoles?.map((option: any) => ({
                label: option.name,
                value: option.name,
              }))}
              onCreateOption={setLenderRole}
              components={{
                ClearIndicator: () => null,
              }}
            />
          </Box> */}
          {/* Borrower Roles */}
          {/* <Box mb="20px">
            <Text as="h3" fontSize="14px" color="black" mb="8px" fontWeight="500">
              Select Borrower Roles
            </Text>
            <CreatableSelect
              useBasicStyles
              selectedOptionColor="black"
              colorScheme="purple"
              variant="outline"
              chakraStyles={selectChakraStyles}
              value={{
                label: borrowerRole,
                value: borrowerRole,
              }}
              hideSelectedOptions
              focusBorderColor={colors.GRAY_50}
              onChange={(newValue: any) => setBorrowerRole(newValue.value)}
              options={borrowerRoles.map((option: string) => ({
                label: option,
                value: option,
              }))}
              onCreateOption={setBorrowerRole}
              components={{
                ClearIndicator: () => null,
              }}
            />
          </Box> */}
          {user.type === "lender" && <ItemContacts contacts={contacts} setContacts={setContacts} />}
          <ItemDocuments documents={documents} setDocuments={setDocuments} item={editItem} />
          <ItemTemplate item={editItem} />
          <FormControl mb="20px">
            <FormLabel>Notes</FormLabel>
            <Textarea
              value={notes}
              onChange={ev => setNotes(ev.target.value)}
              placeholder="Add additional notes here..."
            />
          </FormControl>
        </DrawerBody>
        <DrawerFooter>
          <Flex w="100%" justifyContent="space-between">
            <DeleteItemBtn item={editItem} disabled={!canEdit} />
            <Box>
              <Button variant="outline" mr={3} onClick={closeDrawer} isLoading={loading}>
                Cancel
              </Button>
              <Button
                data-cy="save-checklist-item-btn"
                variant={getCustomButtonVariant(user)}
                onClick={saveChanges}
                isLoading={loading}
              >
                Save Changes
              </Button>
            </Box>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default EditItemDrawer;
